import { ref } from 'vue';
import type { AnalyticsSummary } from '../types';
import { useStrapiGet } from 'src/shared/composables/useStrapiApi';

export function useAnalytics() {
  const summary = ref<AnalyticsSummary | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function fetchMySummary() {
    loading.value = true;
    error.value = null;
    const {
      data,
      execute,
      error: fetchError,
      isFinished,
    } = useStrapiGet('/user-stats/summary-me');
    await execute().catch((e: unknown) => {
      console.error('Auth check failed:', e);
      error.value = (e as Error).message || 'Failed to fetch analytics';
    });

    if (fetchError.value || !data.value) {
      error.value = (fetchError.value as string) || 'Failed to fetch analytics';
      return;
    }
    summary.value = data.value as AnalyticsSummary;
    loading.value = !isFinished.value;
  }

  async function fetchParentSummary() {
    loading.value = true;
    error.value = null;
    const {
      data,
      execute,
      error: fetchError,
      isFinished,
    } = useStrapiGet('/user-stats/summary-parent');
    await execute().catch((e: unknown) => {
      console.error('Parent analytics fetch failed:', e);
      error.value = (e as Error).message || 'Failed to fetch parent analytics';
    });

    if (fetchError.value || !data.value) {
      error.value =
        (fetchError.value as string) || 'Failed to fetch parent analytics';
      return;
    }
    summary.value = data.value as AnalyticsSummary;
    loading.value = !isFinished.value;
  }

  async function fetchTeacherSummary() {
    loading.value = true;
    error.value = null;
    const {
      data,
      execute,
      error: fetchError,
      isFinished,
    } = useStrapiGet('/user-stats/summary-teacher');
    await execute().catch((e: unknown) => {
      console.error('Teacher analytics fetch failed:', e);
      error.value = (e as Error).message || 'Failed to fetch teacher analytics';
    });

    if (fetchError.value || !data.value) {
      error.value =
        (fetchError.value as string) || 'Failed to fetch teacher analytics';
      return;
    }
    summary.value = data.value as AnalyticsSummary;
    loading.value = !isFinished.value;
  }

  async function fetchChildSummary(childDocId: number | string) {
    loading.value = true;
    error.value = null;
    const {
      data,
      execute,
      error: fetchError,
      isFinished,
    } = useStrapiGet(`/user-stats/summary-child/${childDocId}`);
    await execute().catch((e: unknown) => {
      console.error('Child analytics fetch failed:', e);
      error.value = (e as Error).message || 'Failed to fetch child analytics';
    });

    if (fetchError.value || !data.value) {
      error.value =
        (fetchError.value as string) || 'Failed to fetch child analytics';
      return;
    }
    summary.value = data.value as AnalyticsSummary;
    loading.value = !isFinished.value;
  }

  return {
    summary,
    loading,
    error,
    fetchMySummary,
    fetchParentSummary,
    fetchTeacherSummary,
    fetchChildSummary,
  };
}
